/**
 * Panier multi-producteur — état client persisté en localStorage.
 */

import type { QuantityBreak } from '../domain/product-quantity';
import { unitPriceWithBreaks } from '../domain/product-quantity';

export const CART_STORAGE_KEY = 'champtable.cart.v2';
const LEGACY_CART_STORAGE_KEY = 'champtable.cart.v1';

export interface CartLine {
  readonly lineKey: string;
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceType: string;
  readonly unit: string;
  readonly unitPriceCents: number;
  readonly antiWastePercent?: number;
  readonly extraIds: readonly string[];
  readonly extrasLabel?: string | null;
  readonly extraCents?: number;
  readonly baseUnitPriceCents?: number;
  readonly quantityStep?: number;
  readonly minQuantity?: number;
  readonly quantityBreaks?: readonly QuantityBreak[];
}

export interface CartProducerBlock {
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly cardAccepted: boolean;
  readonly lines: readonly CartLine[];
}

/** @deprecated v1 mono-producteur — migré automatiquement vers v2. */
export interface CartStateV1 {
  readonly version: 1;
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly cardAccepted: boolean;
  readonly lines: readonly CartLine[];
  readonly updatedAt: string;
}

export interface CartState {
  readonly version: 2;
  readonly blocks: readonly CartProducerBlock[];
  readonly updatedAt: string;
}

export function cartLineKey(productId: string, extraIds: readonly string[]): string {
  const sorted = [...extraIds].sort().join(',');
  return sorted.length > 0 ? `${productId}:${sorted}` : productId;
}

export function cartLineUnitCents(line: CartLine): number {
  const extraCents = line.extraCents ?? 0;
  const base = line.baseUnitPriceCents ?? line.unitPriceCents - extraCents;
  return unitPriceWithBreaks(base, line.quantity, line.quantityBreaks ?? []) + extraCents;
}

export function cartLineTotalCents(line: CartLine): number {
  return Math.round(line.quantity * cartLineUnitCents(line));
}

export function cartBlockTotalCents(block: CartProducerBlock): number {
  return block.lines.reduce((sum, line) => sum + cartLineTotalCents(line), 0);
}

export function cartTotalCents(state: CartState | null): number {
  if (state === null) {
    return 0;
  }
  return state.blocks.reduce((sum, block) => sum + cartBlockTotalCents(block), 0);
}

export function cartItemCount(state: CartState | null): number {
  if (state === null) {
    return 0;
  }
  return state.blocks.reduce((sum, block) => sum + block.lines.length, 0);
}

export function cartProducerCount(state: CartState | null): number {
  return state?.blocks.length ?? 0;
}

export function formatCartSummary(count: number, totalCents: number, producerCount = 1): string {
  const articles = count === 1 ? '1 article' : `${count} articles`;
  const euros = (totalCents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
  const producers =
    producerCount <= 1 ? '' : ` · ${producerCount} producteurs`;
  return `${articles} · ${euros}${producers}`;
}

export function formatCheckoutSummary(totalCents: number, orderCount: number, producerNames: readonly string[]): string {
  const euros = (totalCents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
  if (orderCount <= 1) {
    return `Vous réglez ${euros} pour 1 commande chez ${producerNames[0] ?? 'ce producteur'}.`;
  }
  return `Vous réglez ${euros} pour ${orderCount} commandes distinctes auprès de ${producerNames.join(', ')}.`;
}

export function migrateCartState(raw: unknown): CartState | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const body = raw as Record<string, unknown>;
  if (body['version'] === 2 && Array.isArray(body['blocks']) && body['blocks'].length > 0) {
    return body as unknown as CartState;
  }
  if (body['version'] === 1 && Array.isArray(body['lines']) && body['lines'].length > 0) {
    const v1 = body as unknown as CartStateV1;
    return {
      version: 2,
      blocks: [
        {
          producerId: v1.producerId,
          producerName: v1.producerName,
          commune: v1.commune,
          cardAccepted: v1.cardAccepted,
          lines: v1.lines,
        },
      ],
      updatedAt: v1.updatedAt,
    };
  }
  return null;
}

export function readStoredCart(): CartState | null {
  try {
    const rawV2 = localStorage.getItem(CART_STORAGE_KEY);
    if (rawV2 !== null) {
      return migrateCartState(JSON.parse(rawV2));
    }
    const rawV1 = localStorage.getItem(LEGACY_CART_STORAGE_KEY);
    if (rawV1 !== null) {
      const migrated = migrateCartState(JSON.parse(rawV1));
      if (migrated !== null) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(migrated));
        localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
      }
      return migrated;
    }
    return null;
  } catch {
    return null;
  }
}

export function toCheckoutBlocks(state: CartState): readonly {
  readonly items: readonly { readonly productId: string; readonly quantity: number; readonly extraIds: readonly string[] }[];
  readonly producerId: string;
  readonly producerName: string;
  readonly cardAccepted: boolean;
  readonly lines: readonly CartLine[];
}[] {
  return state.blocks.map((block) => ({
    producerId: block.producerId,
    producerName: block.producerName,
    cardAccepted: block.cardAccepted,
    lines: block.lines,
    items: block.lines.map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
      extraIds: line.extraIds,
    })),
  }));
}
